import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';


const EMPTY_ITEM = {
  name: '',
  category: '',
  unit: '',
  currentStock: '',
  minStock: '',
  reorderQty: '',
  unitRate: '',
  batchNo: '',
  expiryDate: '',
  notes: '',
};

const inp = { className: 'form-control' };

function F({ label, children, col }) {
  return (
    <div className="form-group" style={col ? { gridColumn: col } : {}}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)',
        width: '100%', maxWidth: wide ? 720 : 500, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid #e8eaed',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>{title}</div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368', lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

function isExpired(expiryDate) {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date(new Date().toDateString());
}

function StockBadge({ current, min, expiryDate }) {
  const cur = Number(current) || 0;
  const mn  = Number(min) || 0;

  if (isExpired(expiryDate)) {
    return (
      <span style={{ background: '#c5221f', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
        ⛔ Expired
      </span>
    );
  }
  if (cur <= 0) {
    return (
      <span style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
        Out of Stock
      </span>
    );
  }
  if (cur < mn) {
    return (
      <span style={{ background: '#fef7e0', color: '#f9ab00', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
        Low Stock
      </span>
    );
  }
  return (
    <span style={{ background: '#e6f4ea', color: '#27ae60', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
      Good
    </span>
  );
}

function AdjustModal({ item, type, onClose, onDone }) {
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) { setErr('Enter a valid quantity'); return; }
    setSaving(true); setErr('');
    try {
      await hospitalApi.adjustStock(item.id, {
        type,
        quantity: parseFloat(quantity),
        notes,
      });
      onDone();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const isOut = type === 'out';
  return (
    <Modal title={`Stock ${isOut ? 'Out' : 'In'} — ${item.name}`} onClose={onClose}>
      {err && (
        <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
          {err}
        </div>
      )}
      <div style={{ marginBottom: 10, fontSize: 13, color: '#5f6368' }}>
        Current stock: <strong style={{ color: DARK }}>{Number(item.current_stock) || 0} {item.unit || ''}</strong>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <F label="Quantity *">
          <input {...inp} type="number" min={0} step="any" value={quantity}
            onChange={e => setQuantity(e.target.value)} autoFocus />
        </F>
        <F label="Notes">
          <textarea {...inp} rows={2} value={notes}
            onChange={e => setNotes(e.target.value)} style={{ resize: 'vertical' }} />
        </F>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 13, color: '#fff',
            background: isOut ? '#e65100' : C,
          }}
        >
          {saving ? 'Saving…' : `Confirm Stock ${isOut ? 'Out' : 'In'}`}
        </button>
        <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
          background: '#fff', cursor: 'pointer', fontSize: 13 }}
          onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

function HistoryModal({ item, onClose }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hospitalApi.getStockMovements(item.id).then(d => {
      if (d) setMovements(d);
      setLoading(false);
    });
  }, [item.id]);

  return (
    <Modal title={`Stock History — ${item.name}`} onClose={onClose} wide>
      {loading ? (
        <div style={{ textAlign: 'center', color: '#9aa0a6', padding: 24 }}>Loading…</div>
      ) : movements.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9aa0a6', padding: 24 }}>No movement history found</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: LIGHT }}>
              {['Date', 'Type', 'Quantity', 'Reference', 'Notes'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {movements.map((m, i) => (
              <tr key={m.id || i} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <td style={{ padding: '10px 12px', color: '#5f6368' }}>{m.movement_date || m.created_at?.split('T')[0] || '—'}</td>
                <td style={{ padding: '10px 12px' }}>
                  {m.type === 'in' ? (
                    <span style={{ color: '#27ae60', fontWeight: 700 }}>▲ In</span>
                  ) : (
                    <span style={{ color: '#c5221f', fontWeight: 700 }}>▼ Out</span>
                  )}
                </td>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>
                  {Number(m.quantity)} {item.unit || ''}
                </td>
                <td style={{ padding: '10px 12px', color: '#5f6368' }}>{m.reference_type || '—'}</td>
                <td style={{ padding: '10px 12px', color: '#5f6368', fontSize: 12 }}>{m.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

function ItemModal({ editItem, onClose, onDone }) {
  const [form, setForm] = useState(editItem ? {
    name: editItem.name || '',
    category: editItem.category || '',
    unit: editItem.unit || '',
    currentStock: editItem.current_stock != null ? String(editItem.current_stock) : '',
    minStock: editItem.min_stock != null ? String(editItem.min_stock) : '',
    reorderQty: editItem.reorder_qty != null ? String(editItem.reorder_qty) : '',
    unitRate: editItem.unit_rate != null ? String(editItem.unit_rate) : '',
    batchNo: editItem.batch_no || '',
    expiryDate: editItem.expiry_date || '',
    notes: editItem.notes || '',
  } : EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Item name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        name: form.name,
        category: form.category,
        unit: form.unit,
        currentStock: parseFloat(form.currentStock) || 0,
        minStock: parseFloat(form.minStock) || 0,
        reorderQty: parseFloat(form.reorderQty) || 0,
        unitRate: parseFloat(form.unitRate) || 0,
        batchNo: form.batchNo,
        expiryDate: form.expiryDate || null,
        notes: form.notes,
      };
      if (editItem) await hospitalApi.updateInventoryItem(editItem.id, payload);
      else await hospitalApi.createInventoryItem(payload);
      onDone();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={editItem ? 'Edit Inventory Item' : 'Add Inventory Item'} onClose={onClose} wide>
      {err && (
        <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
          {err}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        <F label="Item Name *">
          <input {...inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </F>
        <F label="Category">
          <input {...inp} value={form.category} placeholder="e.g. Medicine, Equipment"
            onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
        </F>
        <F label="Unit">
          <input {...inp} value={form.unit} placeholder="e.g. pcs, ml, kg"
            onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
        </F>
        <F label="Opening Stock">
          <input {...inp} type="number" min={0} step="any" value={form.currentStock}
            onChange={e => setForm(f => ({ ...f, currentStock: e.target.value }))} />
        </F>
        <F label="Min Stock">
          <input {...inp} type="number" min={0} step="any" value={form.minStock}
            onChange={e => setForm(f => ({ ...f, minStock: e.target.value }))} />
        </F>
        <F label="Reorder Qty">
          <input {...inp} type="number" min={0} step="any" value={form.reorderQty}
            onChange={e => setForm(f => ({ ...f, reorderQty: e.target.value }))} />
        </F>
        <F label="Unit Rate (₹)">
          <input {...inp} type="number" min={0} step="any" value={form.unitRate}
            onChange={e => setForm(f => ({ ...f, unitRate: e.target.value }))} />
        </F>
        <F label="Batch No">
          <input {...inp} value={form.batchNo} onChange={e => setForm(f => ({ ...f, batchNo: e.target.value }))} />
        </F>
        <F label="Expiry Date">
          <input {...inp} type="date" value={form.expiryDate}
            onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))} />
        </F>
        <F label="Notes" col="1/-1">
          <textarea {...inp} rows={2} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            style={{ resize: 'vertical' }} />
        </F>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : editItem ? 'Update Item' : 'Add Item'}
        </button>
        <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
          background: '#fff', cursor: 'pointer', fontSize: 13 }}
          onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

export default function HospitalInventory({ role }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustType, setAdjustType] = useState('in');
  const [historyTarget, setHistoryTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const load = useCallback(() => {
    hospitalApi.getInventoryItems().then(d => { if (d) setItems(d); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = ['All', ...Array.from(new Set(items.map(it => it.category).filter(Boolean)))];

  const filtered = items.filter(it => {
    const matchSearch = !search || it.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'All' || it.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const lowStockCount = items.filter(it => {
    const cur = Number(it.current_stock) || 0;
    const mn = Number(it.min_stock) || 0;
    return cur > 0 && cur < mn;
  }).length;

  const outOfStockCount = items.filter(it => (Number(it.current_stock) || 0) <= 0).length;
  const alertCount = lowStockCount + outOfStockCount;

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    await hospitalApi.deleteInventoryItem(id);
    load();
  };

  const openAdjust = (item, type) => {
    setAdjustTarget(item);
    setAdjustType(type);
  };

  return (
    <div>
      {alertCount > 0 && (
        <div style={{
          background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '10px 16px',
          marginBottom: 14, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>!</span>
          {outOfStockCount > 0 && <span>{outOfStockCount} item{outOfStockCount > 1 ? 's' : ''} out of stock</span>}
          {outOfStockCount > 0 && lowStockCount > 0 && <span>·</span>}
          {lowStockCount > 0 && <span>{lowStockCount} item{lowStockCount > 1 ? 's' : ''} below minimum stock</span>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          className="form-control"
          placeholder="Search items…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
        <select
          className="form-control"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{ width: 180 }}
        >
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        {role !== 'viewer' && (
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Item
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>
              {items.length === 0 ? 'No inventory items found' : 'No items match the filter'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: LIGHT }}>
                    {['Item Name', 'Category', 'Unit', 'Current Stock', 'Min Stock', 'Unit Rate', 'Batch No', 'Expiry Date', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it, i) => (
                    <tr key={it.id} style={{
                      borderTop: '1px solid #f1f3f4',
                      background: isExpired(it.expiry_date) ? '#fff5f5' : i % 2 === 0 ? '#fff' : '#fafbfc',
                    }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK, whiteSpace: 'nowrap' }}>{it.name}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {it.category ? (
                          <span style={{ background: LIGHT, color: C, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                            {it.category}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>{it.unit || '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: DARK }}>{Number(it.current_stock) || 0}</td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>{Number(it.min_stock) || 0}</td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>
                        {it.unit_rate > 0
                          ? '₹' + Number(it.unit_rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>{it.batch_no || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>{it.expiry_date || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <StockBadge current={it.current_stock} min={it.min_stock} expiryDate={it.expiry_date} />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {role !== 'viewer' && (
                            <>
                              <button
                                style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                                  color: C, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => openAdjust(it, 'in')}>Stock In</button>
                              <button
                                style={{ fontSize: 11, padding: '3px 8px', background: '#fff3e0',
                                  color: '#e65100', border: '1px solid #ffcc80', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => openAdjust(it, 'out')}>Stock Out</button>
                              <button
                                style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                                  color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => setEditTarget(it)}>Edit</button>
                            </>
                          )}
                          <button
                            style={{ fontSize: 11, padding: '3px 8px', background: '#f5f5f5',
                              color: '#5f6368', border: '1px solid #e0e0e0', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => setHistoryTarget(it)}>History</button>
                          {role === 'admin' && (
                            <button
                              style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                                color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => handleDelete(it.id)}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {adjustTarget && (
        <AdjustModal
          item={adjustTarget}
          type={adjustType}
          onClose={() => setAdjustTarget(null)}
          onDone={load}
        />
      )}

      {historyTarget && (
        <HistoryModal
          item={historyTarget}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editTarget && (
        <ItemModal
          editItem={editTarget}
          onClose={() => setEditTarget(null)}
          onDone={load}
        />
      )}

      {showAddModal && (
        <ItemModal
          editItem={null}
          onClose={() => setShowAddModal(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
